import { StreamChat, User } from 'stream-chat';

import { datadogLogError } from 'util/datadogLogs';

import { getToken } from './api';

let globalStreamChatClient: StreamChat | null = null;
const MIN_TOKEN_ERROR_COOLDOWN_MS = 3_000;
const MAX_TOKEN_ERROR_COOLDOWN_MS = 10_000;
let nextTokenRequestAllowedAt: number | null = null;
let inFlightInitializationPromise: Promise<StreamChat> | null = null;

const getRandomCooldownMs = (min: number, max: number): number =>
  Math.floor(Math.random() * (max - min + 1)) + min;

export const initializeStreamChatClient = async (
  apiKey: string,
  user: User
): Promise<StreamChat> => {
  if (!globalStreamChatClient) {
    globalStreamChatClient = StreamChat.getInstance(apiKey);
  }

  if (globalStreamChatClient.userID) {
    return globalStreamChatClient;
  }

  if (inFlightInitializationPromise) {
    return inFlightInitializationPromise;
  }

  inFlightInitializationPromise = (async () => {
    try {
      await globalStreamChatClient!.connectUser(user, async () => {
        if (
          nextTokenRequestAllowedAt &&
          Date.now() < nextTokenRequestAllowedAt
        ) {
          throw new Error(
            'Token temporarily unavailable. Please try again later.'
          );
        }

        try {
          const token = await getToken();
          return token.token;
        } catch (err: any) {
          const status = err?.response?.status;
          datadogLogError({
            message: 'stream_token_fetch_error',
            context: { userId: user.id, status, message: err?.message },
          });

          if (typeof status === 'number' && status >= 400) {
            nextTokenRequestAllowedAt =
              Date.now() +
              getRandomCooldownMs(
                MIN_TOKEN_ERROR_COOLDOWN_MS,
                MAX_TOKEN_ERROR_COOLDOWN_MS
              );
          }
          throw err;
        }
      });

      return globalStreamChatClient!;
    } catch (err: any) {
      const status = err?.response?.status;
      datadogLogError({
        message: 'stream_connect_user_error',
        context: { userId: user.id, status, message: err?.message },
      });
      throw err;
    }
  })();

  try {
    return await inFlightInitializationPromise;
  } finally {
    inFlightInitializationPromise = null;
  }
};

export const getStreamChatClient = () => globalStreamChatClient;
